import React, { useEffect, useMemo, useState, Component } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SubscriptionTable } from '../components/SubscriptionTable';
import { StatCard, DateRangeSelector, TrendIndicator } from '../components/StatCard';
import { NavBar } from '../components/SubscriptionAppNavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Tabs } from '../components/Tabs';
import { BulkUpdateJobsTab } from '../components/BulkUpdateJobs';
import { DateRangeTabs, DateRangeOption } from '../components/DateRangeTabs';
import { Plus, CreditCard, AlertTriangle, TrendingUp, TrendingDown, List, CalendarDays } from 'lucide-react';
import { SubscriptionCalendar } from '../components/SubscriptionCalendar';
import { useAppData } from '../contexts/AppDataContext';
import { CreateSubscriptionModal } from '../components/CreateSubscriptionModal';
import { calculateDaysUntilExpiry } from '../data/mockSubscriptionData';
import { AnalyticsPanel } from '../components/AnalyticsPanel';
// Contextual Trend Indicator Component
interface ContextualTrendProps {
  value: number;
  label: string;
  direction: 'up' | 'down';
  isInverse?: boolean;
}
function ContextualTrend({
  value,
  label,
  direction,
  isInverse = false
}: ContextualTrendProps) {
  const isPositiveChange = isInverse ? direction === 'down' : direction === 'up';
  const color = isPositiveChange ? '#10B981' : '#EF4444';
  const Icon = direction === 'up' ? TrendingUp : TrendingDown;
  return <div className="flex items-center gap-1.5" data-id="element-13394" data-mp="1zmuan7aazkem">
      <Icon className="w-3 h-3" style={{
      color
    }} data-id="element-13395" data-mp="1zp6trnrss0t9" />
      <span className="text-xs font-medium" style={{
      color
    }} data-id="element-13396" data-mp="1up2chvj2f21z">
        {direction === 'up' ? '+' : '-'}
        {Math.abs(value)}%
      </span>
      <span className="text-xs text-[#94A3B8]" data-id="element-13397" data-mp="3p20g5wa9f0gc">{label}</span>
    </div>;
}
const DASHBOARD_TABS = [{
  id: 'subscriptions',
  label: 'Subscriptions'
}, {
  id: 'jobs',
  label: 'Bulk Update Jobs'
}];
export function SubscriptionDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    subscriptions
  } = useAppData();
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [searchQuery, setSearchQuery] = useState('');
  const [upcomingRevenueDateRange, setUpcomingRevenueDateRange] = useState('Next 30 days');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    status: [],
    id: [],
    customer: [],
    amount: [],
    subscriptionTitle: [],
    nextOrderDate: [],
    startDate: []
  });
  const [selectedRange, setSelectedRange] = useState<DateRangeOption>('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isAnalyticsPanelOpen, setIsAnalyticsPanelOpen] = useState(true);
  const [subscriptionsView, setSubscriptionsView] = useState<'list' | 'calendar'>('list');
  const [calendarFocusDate, setCalendarFocusDate] = useState<string | null>(null);
  // Clicking a calendar day filters the list to that next billing day
  const handleCalendarDaySelect = (isoDate: string) => {
    setFilterValues(prev => ({
      ...prev,
      nextBillingDate: [isoDate]
    }));
    setCalendarFocusDate(null);
    setSubscriptionsView('list');
  };
  const getComparisonLabel = (range: DateRangeOption) => {
    switch (range) {
      case 'today':
        return 'vs yesterday';
      case '7days':
        return 'vs prev 7 days';
      case '30days':
        return 'vs prev 30 days';
      case '90days':
        return 'vs prev 90 days';
      case 'year':
        return 'vs last year';
      case 'custom':
        return 'vs prior period';
      default:
        return 'vs last month';
    }
  };
  const handleCustomDateChange = (start: string, end: string) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };
  useEffect(() => {
    // A bulk action can hand us straight to the jobs tab after submitting.
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
    if (location.state?.filterValues) {
      setFilterValues(prev => ({
        ...prev,
        ...location.state.filterValues
      }));
      // A filtered navigation should always land on the list itself.
      if (!location.state.activeTab) {
        setActiveTab('subscriptions');
      }
    }
    // The dashboard's "Next 7 days" strip opens the calendar, optionally on a day
    if (location.state?.subscriptionsView === 'calendar') {
      setActiveTab('subscriptions');
      setSubscriptionsView('calendar');
      setCalendarFocusDate(location.state.focusDate ?? null);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const upcomingRevenue = useMemo(() => {
    const now = new Date();
    let daysLimit = 30;
    if (upcomingRevenueDateRange === 'Next 7 days') {
      daysLimit = 7;
    } else if (upcomingRevenueDateRange === 'Next 30 days') {
      daysLimit = 30;
    } else if (upcomingRevenueDateRange === 'Next 3 months') {
      daysLimit = 90;
    }
    const revenue = subscriptions.filter(sub => {
      const paymentDate = new Date(sub.nextPayment);
      const daysDiff = Math.ceil((paymentDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff >= 0 && daysDiff <= daysLimit;
    }).reduce((total, sub) => {
      const amount = parseFloat(sub.amount.replace('$', '').replace(',', ''));
      return total + amount;
    }, 0);
    return `$${revenue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  }, [upcomingRevenueDateRange, subscriptions]);
  const expiringCardsStats = useMemo(() => {
    const expiringCards = subscriptions.filter(sub => {
      if (!sub.cardExpiry) return false;
      const daysUntilExpiry = calculateDaysUntilExpiry(sub.cardExpiry.expiryMonth, sub.cardExpiry.expiryYear);
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 60;
    });
    const thisMonth = expiringCards.filter(sub => {
      if (!sub.cardExpiry) return false;
      const daysUntilExpiry = calculateDaysUntilExpiry(sub.cardExpiry.expiryMonth, sub.cardExpiry.expiryYear);
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
    }).length;
    const nextMonth = expiringCards.filter(sub => {
      if (!sub.cardExpiry) return false;
      const daysUntilExpiry = calculateDaysUntilExpiry(sub.cardExpiry.expiryMonth, sub.cardExpiry.expiryYear);
      return daysUntilExpiry > 30 && daysUntilExpiry <= 60;
    }).length;
    return {
      total: expiringCards.length,
      thisMonth,
      nextMonth
    };
  }, [subscriptions]);
  const handleNavigateToReports = () => {
    let filterValue = 'next-30-days';
    if (upcomingRevenueDateRange === 'Next 7 days') {
      filterValue = 'next-7-days';
    } else if (upcomingRevenueDateRange === 'Next 30 days') {
      filterValue = 'next-30-days';
    } else if (upcomingRevenueDateRange === 'Next 3 months') {
      filterValue = 'next-3-months';
    }
    setFilterValues({
      status: [],
      id: [],
      customer: [],
      amount: [],
      subscriptionTitle: [],
      nextOrderDate: [filterValue],
      startDate: []
    });
  };
  const handleNavigateToAtRisk = () => {
    setFilterValues({
      status: ['at-risk'],
      id: [],
      customer: [],
      amount: [],
      subscriptionTitle: [],
      nextOrderDate: [],
      startDate: []
    });
  };
  const handleNavigateToOnHold = () => {
    setFilterValues({
      status: ['on-hold'],
      id: [],
      customer: [],
      amount: [],
      subscriptionTitle: [],
      nextOrderDate: [],
      startDate: []
    });
  };
  const handleNavigateToExpiringCards = () => {
    setFilterValues({
      status: [],
      paymentStatus: [],
      expiringCards: ['expiring-soon'],
      tags: [],
      id: [],
      customer: [],
      amount: [],
      subscriptionTitle: [],
      nextOrderDate: [],
      startDate: []
    });
  };
  const handleCreateSubscription = () => {
    setIsCreateModalOpen(true);
  };
  const handleSubscriptionCreated = (subscriptionId: string) => {
    setIsCreateModalOpen(false);
  };
  const filteredSubscriptions = useMemo(() => {
    let filtered = subscriptions;
    if (filterValues.status && filterValues.status.length > 0) {
      filtered = filtered.filter(sub => filterValues.status.some(val => sub.status.toLowerCase() === val.toLowerCase().replace('-', ' ')));
    }
    if (filterValues.paymentStatus && filterValues.paymentStatus.length > 0) {
      filtered = filtered.filter(sub => {
        const status = sub.paymentInfo?.status || 'healthy';
        return filterValues.paymentStatus.some(val => status === val);
      });
    }
    if (filterValues.expiringCards && filterValues.expiringCards.length > 0) {
      filtered = filtered.filter(sub => {
        if (!sub.cardExpiry) return false;
        const expiryDate = new Date(sub.cardExpiry.expiryYear, sub.cardExpiry.expiryMonth, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffMs = expiryDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 60;
      });
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sub => sub.customerName.toLowerCase().includes(query) || sub.customer.toLowerCase().includes(query) || sub.id.toLowerCase().includes(query));
    }
    return filtered;
  }, [subscriptions, filterValues, searchQuery]);
  return <div className="bg-[#F1F5F9] min-h-full p-4 md:p-6" data-id="element-13398" data-mp="17suitjj59vy8">
      <div className="mb-4 md:mb-6" data-id="element-13399" data-mp="12uax9k8gqm6e">
        <NavBar tabs={[]} title="Subscription Dashboard" data-id="element-13400" data-mp="12vclyriej76l" />
      </div>

      {/* Tabs */}
      <div className="mb-6" data-id="element-13401" data-mp="xtwzqw4hmumm">
        <Tabs tabs={DASHBOARD_TABS} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-13402" data-mp="xw9d0vzawgtc" />
      </div>

      {activeTab === 'jobs' ? <BulkUpdateJobsTab data-id="element-13403" data-mp="2s7xfxifhpd3a" /> : <>
          {/* Page Description */}
          <div className="mb-6 md:mb-8" data-id="element-13404" data-mp="2s5uiwefx6jp2">
            <p className="text-[14px] md:text-[16px] text-[#475569]" data-id="element-13405" data-mp="2n9w159p3jzxm">
              Manage your wine club members and their subscription settings
            </p>
          </div>

          {/* Date Range Tabs */}
          <div className="mb-6" data-id="element-13406" data-mp="2wzyrt38qx1ss">
            <DateRangeTabs value={selectedRange} onChange={setSelectedRange} customStartDate={customStartDate} customEndDate={customEndDate} onCustomDateChange={handleCustomDateChange} data-id="element-13407" data-mp="2x10fq8av1irx" />
          </div>

          {/* Actionable Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6" data-id="element-13408" data-mp="2iaj2lrov54f7">
            {/* Card 1: Upcoming Revenue */}
            <StatCard label="Upcoming Revenue" value={upcomingRevenue} dateRange={<DateRangeSelector value={upcomingRevenueDateRange} onChange={setUpcomingRevenueDateRange} options={['Next 7 days', 'Next 30 days', 'Next 3 months']} data-id="element-13410" data-mp="3todgq344e1sa" />} onClick={handleNavigateToReports} data-id="element-13409" data-mp="2d9d93rndpd4m" />

            {/* Card 2: At Risk */}
            <StatCard label="At Risk" value={4} secondaryValue="$1,240 at risk" badge={{
          text: 'In Retry',
          variant: 'error'
        }} onClick={handleNavigateToAtRisk} data-id="element-13411" data-mp="10epkp81alqb" />

            {/* Card 3: On Hold */}
            <StatCard label="On Hold" value={2} badge={{
          text: 'Action Required',
          variant: 'warning'
        }} onClick={handleNavigateToOnHold} data-id="element-13412" data-mp="3s7zmr50c6kln" />

            {/* Card 4: Expiring Cards */}
            <StatCard label="Expiring Cards" value={expiringCardsStats.total} secondaryValue={`${expiringCardsStats.thisMonth} this month • ${expiringCardsStats.nextMonth} next month`} badge={expiringCardsStats.total > 0 ? {
          text: 'Action Needed',
          variant: 'warning'
        } : undefined} onClick={handleNavigateToExpiringCards} icon={<CreditCard className="w-5 h-5 text-[#F59E0B]" data-id="element-13414" data-mp="12qa4uqo9nps5" />} data-id="element-13413" data-mp="or0zkm1lmg2y" />
          </div>

          <hr className="my-6 md:my-10 border-[#CBD5E1]" data-id="element-13415" data-mp="17i1yja2pybmi" />

          {/* List / Calendar toggle */}
          <div className="flex justify-end mb-4" data-id="element-13416" data-mp="1hcpkznrzau1t">
            <div className="inline-flex rounded-md border border-[#CBD5E1] bg-white p-1" role="group" aria-label="Subscriptions view" data-id="element-13417" data-mp="1hbeidra9rsca">
              {[{
            id: 'list' as const,
            label: 'List',
            icon: List
          }, {
            id: 'calendar' as const,
            label: 'Calendar',
            icon: CalendarDays
          }].map(option => {
            const Icon = option.icon;
            const isActive = subscriptionsView === option.id;
            return <button key={option.id} type="button" onClick={() => setSubscriptionsView(option.id)} aria-pressed={isActive} className={`inline-flex items-center gap-1.5 px-3 h-8 rounded text-[13px] font-medium transition-colors ${isActive ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-13418" data-mp="tm3cjve2b7ra">
                    <Icon className="w-4 h-4" data-id="element-13419" data-mp="apbxjy5jzet1" />
                    {option.label}
                  </button>;
          })}
            </div>
          </div>

          {subscriptionsView === 'calendar' ? <SubscriptionCalendar focusDate={calendarFocusDate} onSelectDay={handleCalendarDaySelect} data-id="element-13420" data-mp="2mf08b78ihb5e" /> : <SubscriptionTable searchQuery={searchQuery} onSearchChange={setSearchQuery} filterValues={filterValues} onFilterValuesChange={setFilterValues} createButton={<Button variant="primary" size="md" onClick={handleCreateSubscription} iconLeft={<Plus className="w-4 h-4" data-id="element-13423" data-mp="23mplemu95nsx" />} data-id="element-13422" data-mp="30bds4n0ipon1">
                Create Subscription
              </Button>} data-id="element-13421" data-mp="2rdv0v0wa6e2p" />}
        </>}

      {/* Create Subscription Modal */}
      <CreateSubscriptionModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={handleSubscriptionCreated} data-id="element-13424" data-mp="27lonlzoln6go" />
    </div>;
}